import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, FileText, Pencil, Plus, Trash2, Loader2, GripVertical, X, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import Navbar from "@/components/Navbar";

const EMPTY_FIELD = { key: "", label: "", type: "text", required: false, placeholder: "" };
const makeField = () => ({ ...EMPTY_FIELD, _uid: (crypto.randomUUID?.() || Math.random().toString(36).slice(2)) });
const EMPTY = {
  id: "", code: "", title: "", description: "", category: "Lainnya", audience: "Umum",
  signature_style: "single-right", body_text: "",
  left_label: "Yang diberi Kuasa", right_label: "Yang membuat Pernyataan",
  extra_fields: [],
};

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function AdminForms() {
  const [items, setItems] = useState([]);
  const [builtinIds, setBuiltinIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const isBuiltin = (id) => builtinIds.includes(id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/forms");
      setItems(data.items || []);
      setBuiltinIds(data.builtin_ids || []);
    } catch { toast.error("Gagal memuat formulir"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY, extra_fields: [] }); setDialogOpen(true); };
  const openEdit = (f) => {
    setEditing(f.id);
    const fields = (f.extra_fields || []).map((x) => ({ ...x, _uid: x._uid || (crypto.randomUUID?.() || Math.random().toString(36).slice(2)) }));
    setForm({ ...EMPTY, ...f, extra_fields: fields });
    setDialogOpen(true);
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const addField = () => setForm((p) => ({ ...p, extra_fields: [...p.extra_fields, makeField()] }));
  const removeField = (i) => setForm((p) => ({ ...p, extra_fields: p.extra_fields.filter((_, idx) => idx !== i) }));
  const updateField = (i, k, v) => setForm((p) => {
    const next = [...p.extra_fields];
    next[i] = { ...next[i], [k]: v };
    if (k === "label" && !next[i].key) next[i].key = slugify(v);
    return { ...p, extra_fields: next };
  });

  const save = async () => {
    if (!form.title || !form.description) { toast.error("Judul dan deskripsi wajib diisi"); return; }
    for (const f of form.extra_fields) {
      if (!f.label || !f.key) { toast.error("Setiap field butuh label & key"); return; }
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/forms/${editing}`, form);
      else await api.post("/admin/forms", form);
      toast.success(editing ? "Formulir diperbarui" : "Formulir baru dibuat");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/forms/${id}`); toast.success("Formulir dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  const builtinLocked = editing && isBuiltin(editing);

  return (
    <div className="min-h-screen bg-slate-50/60">
      <Navbar variant="admin" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Kembali ke Dashboard
          </Link>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-600 font-semibold">
                <FileText className="w-3.5 h-3.5" /> Kelola Formulir
              </div>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">Daftar Formulir</h1>
              <p className="mt-1 text-slate-600">Tambah formulir baru dengan field dinamis tanpa menyentuh kode.</p>
            </div>
            <Button onClick={openCreate} className="btn-lift rounded-lg bg-blue-600 hover:bg-blue-700" data-testid="form-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Tambah Formulir
            </Button>
          </div>

          <div className="mt-8 rounded-2xl bg-white border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="p-12 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kode</TableHead>
                    <TableHead>Judul</TableHead>
                    <TableHead>Kategori</TableHead>
                    <TableHead>Field</TableHead>
                    <TableHead>Jenis</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">Belum ada formulir.</TableCell></TableRow>
                  )}
                  {items.map((f) => (
                    <TableRow key={f.id} data-testid={`form-row-${f.id}`}>
                      <TableCell><Badge variant="outline" className="font-mono text-xs">{f.code}</Badge></TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900 text-sm">{f.title}</div>
                        <div className="text-xs text-slate-500 line-clamp-1">{f.description}</div>
                      </TableCell>
                      <TableCell><Badge variant="outline">{f.category}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-600">{(f.extra_fields || []).length} field</TableCell>
                      <TableCell>
                        {isBuiltin(f.id) ? (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 border hover:bg-amber-50"><Lock className="w-3 h-3 mr-1" /> Bawaan</Badge>
                        ) : (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border hover:bg-emerald-50">Custom</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(f)} data-testid={`form-edit-${f.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {!isBuiltin(f.id) && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" data-testid={`form-delete-${f.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Hapus formulir?</AlertDialogTitle>
                                <AlertDialogDescription><b>{f.title}</b> akan dihapus permanen.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Batal</AlertDialogCancel>
                                <AlertDialogAction onClick={() => del(f.id)} className="bg-red-500 hover:bg-red-600">Hapus</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </motion.div>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Ubah Formulir" : "Tambah Formulir"}</DialogTitle>
            <DialogDescription>
              {builtinLocked
                ? "Formulir bawaan: hanya metadata (judul, deskripsi, kategori) yang dapat diubah agar layout PDF tetap sesuai aslinya."
                : "Buat formulir baru. Daftar field di bawah akan tampil di wizard pemohon dan dicetak otomatis pada PDF."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label>Kode <span className="text-red-500">*</span></Label>
              <Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="SRK-01" className="mt-1.5 font-mono" disabled={builtinLocked} data-testid="form-field-code" />
            </div>
            <div>
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="form-field-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Kuasa">Kuasa</SelectItem>
                  <SelectItem value="Pernyataan">Pernyataan</SelectItem>
                  <SelectItem value="Permohonan">Permohonan</SelectItem>
                  <SelectItem value="Lainnya">Lainnya</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Judul Formulir <span className="text-red-500">*</span></Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Surat Keterangan Kehilangan Paspor" className="mt-1.5" data-testid="form-field-title" />
            </div>
            <div className="col-span-2">
              <Label>Deskripsi <span className="text-red-500">*</span></Label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Deskripsi singkat untuk ditampilkan di kartu formulir." className="mt-1.5" data-testid="form-field-desc" />
            </div>
            <div>
              <Label>Audiens</Label>
              <Select value={form.audience} onValueChange={(v) => set("audience", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Umum">Umum</SelectItem>
                  <SelectItem value="Dewasa">Dewasa</SelectItem>
                  <SelectItem value="Anak">Anak</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Gaya Tanda Tangan</Label>
              <Select value={form.signature_style} onValueChange={(v) => set("signature_style", v)} disabled={builtinLocked}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single-right">Tunggal (kanan) — materai di pemohon</SelectItem>
                  <SelectItem value="double">Dua kolom (pemberi + penerima kuasa)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Kalimat Penutup (opsional)</Label>
              <Textarea value={form.body_text || ""} onChange={(e) => set("body_text", e.target.value)} placeholder="Demikian surat ini saya buat..." className="mt-1.5 min-h-[70px]" disabled={builtinLocked} />
            </div>
          </div>

          {!builtinLocked && (
            <div className="pt-2">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-slate-900 tracking-tight">Field Tambahan</h4>
                  <p className="text-xs text-slate-500">Daftar field yang harus diisi pemohon di luar biodata umum.</p>
                </div>
                <Button variant="outline" size="sm" onClick={addField} className="rounded-lg" data-testid="form-add-field-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Tambah Field
                </Button>
              </div>
              <div className="space-y-3">
                {form.extra_fields.map((f, i) => (
                  <div key={f._uid || `${i}-${f.key || 'new'}`} className="rounded-xl border border-slate-200 bg-slate-50/50 p-3 grid grid-cols-12 gap-2 items-center" data-testid={`form-field-row-${i}`}>
                    <GripVertical className="w-4 h-4 text-slate-300 col-span-1" />
                    <Input value={f.label} onChange={(e) => updateField(i, "label", e.target.value)} placeholder="Label field" className="col-span-4" />
                    <Input value={f.key} onChange={(e) => updateField(i, "key", slugify(e.target.value))} placeholder="key_slug" className="col-span-3 font-mono text-xs" />
                    <Select value={f.type} onValueChange={(v) => updateField(i, "type", v)}>
                      <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Teks</SelectItem>
                        <SelectItem value="textarea">Teks Panjang</SelectItem>
                        <SelectItem value="date">Tanggal</SelectItem>
                        <SelectItem value="tel">Telepon</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                      </SelectContent>
                    </Select>
                    <label className="col-span-1 flex items-center gap-1.5 text-xs text-slate-600">
                      <Checkbox checked={f.required} onCheckedChange={(v) => updateField(i, "required", !!v)} /> Wajib
                    </label>
                    <Button variant="ghost" size="sm" onClick={() => removeField(i)} className="col-span-1 text-red-500">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {form.extra_fields.length === 0 && (
                  <div className="rounded-xl border border-dashed border-slate-300 py-6 text-center text-sm text-slate-500">Belum ada field tambahan.</div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="pt-4">
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} className="btn-lift bg-blue-600 hover:bg-blue-700" data-testid="form-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
